import { useState, useEffect, useRef, useCallback } from 'react'
import { AlertTriangle, ExternalLink, X } from 'lucide-react'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useDuplicateCheck } from '@/hooks/use-api'
import type { DuplicateCheckResult } from '@/types'
import { SEMANTIC, CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'

interface DuplicateWarningProps {
  entityType: 'Account' | 'Contact' | 'Lead'
  formData: Record<string, unknown>
  triggerFields?: string[]
}

const DEBOUNCE_MS = 500

const confidenceBadgeStyle: Record<
  string,
  { backgroundColor: string; color: string; borderColor: string }
> = {
  High: {
    backgroundColor: CATEGORICAL.raspberry.tint,
    color: GRACKLE_COLORS.prussianBlue,
    borderColor: CATEGORICAL.raspberry.solid,
  },
  Medium: {
    backgroundColor: CATEGORICAL.clay.tint,
    color: GRACKLE_COLORS.prussianBlue,
    borderColor: CATEGORICAL.clay.solid,
  },
  Low: {
    backgroundColor: CATEGORICAL.slate.tint,
    color: GRACKLE_COLORS.prussianBlue,
    borderColor: CATEGORICAL.slate.solid,
  },
}

function getEntityUrl(entityType: string, id: string): string {
  const routes: Record<string, string> = {
    Account: '/accounts',
    Contact: '/contacts',
    Lead: '/leads',
  }
  return `${routes[entityType] || '/'}/${id}`
}

function getDefaultTriggerFields(entityType: string): string[] {
  switch (entityType) {
    case 'Account':
      return ['name', 'phone', 'website']
    case 'Contact':
      return ['first_name', 'last_name', 'email', 'phone']
    case 'Lead':
      return ['first_name', 'last_name', 'email', 'company', 'phone']
    default:
      return ['name', 'email']
  }
}

function hasMinimumData(entityType: string, data: Record<string, unknown>): boolean {
  const name = String(data.name || data.last_name || '').trim()
  const email = String(data.email || '').trim()
  const phone = String(data.phone || '').trim()

  if (entityType === 'Account') {
    return name.length >= 2 || phone.length >= 7
  }
  // Contact or Lead: need name or email
  return name.length >= 2 || (email.includes('@') && email.length >= 5)
}

export function DuplicateWarning({ entityType, formData, triggerFields }: DuplicateWarningProps) {
  const [dismissed, setDismissed] = useState(false)
  const [result, setResult] = useState<DuplicateCheckResult | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastDataRef = useRef<string>('')

  const checkMutation = useDuplicateCheck()
  const fields = triggerFields || getDefaultTriggerFields(entityType)

  const runCheck = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        const checkResult = await checkMutation.mutateAsync({ entityType, data })
        setResult(checkResult)
        setDismissed(false)
      } catch {
        // Silently fail — duplicate warning is advisory
      }
    },
    [entityType, checkMutation]
  )

  useEffect(() => {
    // Build a fingerprint from trigger fields
    const fingerprint = fields.map((f) => String(formData[f] || '')).join('|')

    // Skip if nothing changed
    if (fingerprint === lastDataRef.current) return
    lastDataRef.current = fingerprint

    // Skip if not enough data
    if (!hasMinimumData(entityType, formData)) {
      setResult(null)
      return
    }

    // Debounce the check
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      runCheck(formData)
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [fields.map((f) => formData[f]).join('|')]) // eslint-disable-line react-hooks/exhaustive-deps

  // Nothing to show
  const duplicates = result?.duplicates || []
  const crossEntity = result?.crossEntityMatches || []
  const totalMatches = duplicates.length + crossEntity.length

  if (dismissed || totalMatches === 0) return null

  return (
    <div aria-live="polite">
      <Alert
        variant="warning"
        className="relative"
        style={{
          backgroundColor: SEMANTIC.warning.fill,
          color: SEMANTIC.warning.text,
          borderColor: GRACKLE_COLORS.pollen,
        }}
      >
        <AlertTriangle
          className="h-4 w-4"
          aria-hidden="true"
          style={{ color: CATEGORICAL.clay.solid }}
        />
        <AlertTitle className="pr-8">
          Potential {totalMatches === 1 ? 'Duplicate' : 'Duplicates'} Found
        </AlertTitle>
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 h-6 w-6 p-0"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss duplicate warning"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
        <AlertDescription>
          <div className="space-y-2 mt-2">
            {duplicates.map((dup) => (
              <div
                key={dup.id}
                className="flex items-center justify-between gap-2 p-2 bg-white/60 rounded border"
                style={{ borderColor: GRACKLE_COLORS.pollen }}
              >
                <div className="flex-1 min-w-0">
                  <a
                    href={getEntityUrl(entityType, dup.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    aria-label={`View ${dup.name} (${dup.confidence_level} confidence match)`}
                  >
                    {dup.name}
                    <ExternalLink className="inline h-3 w-3 ml-1" aria-hidden="true" />
                  </a>
                  {dup.email && (
                    <p className="text-xs text-muted-foreground truncate">{dup.email}</p>
                  )}
                  {dup.phone && !dup.email && (
                    <p className="text-xs text-muted-foreground truncate">{dup.phone}</p>
                  )}
                </div>
                <Badge
                  variant="outline"
                  className="text-xs flex-shrink-0"
                  style={confidenceBadgeStyle[dup.confidence_level]}
                >
                  {dup.confidence_score}% {dup.confidence_level}
                </Badge>
              </div>
            ))}

            {crossEntity.length > 0 && (
              <>
                <p
                  className="text-xs font-semibold mt-3"
                  style={{ color: GRACKLE_COLORS.prussianBlue }}
                >
                  This person may already exist as a Contact:
                </p>
                {crossEntity.map((match) => (
                  <div
                    key={match.id}
                    className="flex items-center justify-between gap-2 p-2 bg-white/60 rounded border"
                    style={{ borderColor: GRACKLE_COLORS.pollen }}
                  >
                    <div className="flex-1 min-w-0">
                      <a
                        href={getEntityUrl('Contact', match.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                        aria-label={`View existing contact ${match.name} (${match.confidence_level} confidence match)`}
                      >
                        {match.name} (Contact)
                        <ExternalLink className="inline h-3 w-3 ml-1" aria-hidden="true" />
                      </a>
                      {match.email && (
                        <p className="text-xs text-muted-foreground truncate">{match.email}</p>
                      )}
                    </div>
                    <Badge
                      variant="outline"
                      className="text-xs flex-shrink-0"
                      style={confidenceBadgeStyle[match.confidence_level]}
                    >
                      {match.confidence_score}% {match.confidence_level}
                    </Badge>
                  </div>
                ))}
              </>
            )}
          </div>
        </AlertDescription>
      </Alert>
    </div>
  )
}
